import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, ClipboardList, Info, Plus } from 'lucide-react'
import {
  useFormulario,
  useCrearPaso, useEditarPaso, useEliminarPaso, useReordenarPasos,
  useCrearCampo, useEditarCampo, useEliminarCampo, useReordenarCampos,
} from '../hooks/useFormBuilder'
import { PasoCard } from '../components/formbuilder/PasoCard'
import { PasoDialog } from '../components/formbuilder/PasoDialog'
import { CampoDialog } from '../components/formbuilder/CampoDialog'
import type { PasoFormulario, CampoFormulario, PasoCreatePayload, TipoCampo } from '../types'

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)

  const [showInfo, setShowInfo] = useState(false)
  const { setOverride, clearOverride } = useBreadcrumb()
  useDocumentTitle('Form Builder')

  const { data: formulario, isLoading } = useFormulario(campaniaId)

  useEffect(() => {
    if (formulario?.campania_titulo) {
      setOverride(`/muestras-medicas/${id}`, formulario.campania_titulo)
    }
    return () => { clearOverride(`/muestras-medicas/${id}`) }
  }, [formulario?.campania_titulo, id, setOverride, clearOverride])

  // Paso mutations
  const crearPaso = useCrearPaso(campaniaId)
  const editarPaso = useEditarPaso(campaniaId)
  const eliminarPaso = useEliminarPaso(campaniaId)
  const reordenarPasos = useReordenarPasos(campaniaId)

  // Campo mutations
  const crearCampo = useCrearCampo(campaniaId)
  const editarCampo = useEditarCampo(campaniaId)
  const eliminarCampo = useEliminarCampo(campaniaId)
  const reordenarCampos = useReordenarCampos(campaniaId)

  // Paso dialog state
  const [pasoDialogOpen, setPasoDialogOpen] = useState(false)
  const [editingPaso, setEditingPaso] = useState<PasoFormulario | null>(null)

  // Campo dialog state
  const [campoDialogOpen, setCampoDialogOpen] = useState(false)
  const [editingCampo, setEditingCampo] = useState<CampoFormulario | null>(null)
  const [activePasoId, setActivePasoId] = useState<number>(0)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  // Paso handlers
  const handleCreatePaso = async (data: PasoCreatePayload) => {
    await crearPaso.mutateAsync(data)
    setPasoDialogOpen(false)
  }

  const handleEditPaso = async (data: PasoCreatePayload) => {
    if (!editingPaso) return
    await editarPaso.mutateAsync({ pasoId: editingPaso.id, payload: data })
    setEditingPaso(null)
  }

  const handleDeletePaso = (pasoId: number) => {
    if (confirm('¿Eliminar este paso y todos sus campos?')) {
      eliminarPaso.mutate(pasoId)
    }
  }

  const handlePasoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !formulario) return

    const oldIndex = formulario.pasos.findIndex((p) => p.id === active.id)
    const newIndex = formulario.pasos.findIndex((p) => p.id === over.id)
    const newOrder = arrayMove(formulario.pasos, oldIndex, newIndex)
    reordenarPasos.mutate(newOrder.map((p) => p.id))
  }

  // Campo handlers
  const handleCreateCampo = async (data: {
    nombre_campo: string
    etiqueta: string
    tipo_campo: TipoCampo
    placeholder?: string
    requerido?: boolean
    configuracion_extra?: Record<string, unknown>
  }) => {
    await crearCampo.mutateAsync({ ...data, paso_id: activePasoId })
    setCampoDialogOpen(false)
  }

  const handleEditCampoSubmit = async (data: {
    nombre_campo: string
    etiqueta: string
    tipo_campo: TipoCampo
    placeholder?: string
    requerido?: boolean
    configuracion_extra?: Record<string, unknown>
  }) => {
    if (!editingCampo) return
    await editarCampo.mutateAsync({ campoId: editingCampo.id, payload: data })
    setEditingCampo(null)
  }

  const handleDeleteCampo = (campoId: number) => {
    if (confirm('¿Eliminar este campo?')) {
      eliminarCampo.mutate(campoId)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campaniaId}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-teal-500" />
            Form Builder
          </h1>
          {formulario && (
            <>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-5 w-5" />
              </button>
              {showInfo && (
                <span className="text-muted-foreground text-sm animate-in slide-in-from-left-2 duration-200">
                  {formulario.campania_titulo}
                </span>
              )}
            </>
          )}
        </div>
        <Button className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => { setEditingPaso(null); setPasoDialogOpen(true) }}>
          <Plus className="mr-2 h-4 w-4" /> Agregar Paso
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : formulario && formulario.pasos.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePasoDragEnd}>
          <SortableContext items={formulario.pasos.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {formulario.pasos.map((paso) => (
                <PasoCard
                  key={paso.id}
                  paso={paso}
                  onEditPaso={() => { setEditingPaso(paso); setPasoDialogOpen(true) }}
                  onDeletePaso={() => handleDeletePaso(paso.id)}
                  onAddCampo={() => { setActivePasoId(paso.id); setEditingCampo(null); setCampoDialogOpen(true) }}
                  onEditCampo={(campo) => { setActivePasoId(paso.id); setEditingCampo(campo); setCampoDialogOpen(true) }}
                  onDeleteCampo={handleDeleteCampo}
                  onReorderCampos={(orden) => reordenarCampos.mutate(orden)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-center py-12">
          <ClipboardList className="mx-auto h-12 w-12 text-teal-500" />
          <h3 className="mt-4 text-lg font-semibold">Sin pasos</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Agrega el primer paso del formulario
          </p>
          <Button className="mt-4 bg-teal-800 hover:bg-teal-900 text-white" onClick={() => { setEditingPaso(null); setPasoDialogOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" /> Agregar Paso
          </Button>
        </div>
      )}

      {/* Paso Dialog */}
      <PasoDialog
        open={pasoDialogOpen}
        onOpenChange={(open) => { setPasoDialogOpen(open); if (!open) setEditingPaso(null) }}
        onSubmit={editingPaso ? handleEditPaso : handleCreatePaso}
        isSubmitting={crearPaso.isPending || editarPaso.isPending}
        paso={editingPaso}
      />

      {/* Campo Dialog */}
      <CampoDialog
        open={campoDialogOpen}
        onOpenChange={(open) => { setCampoDialogOpen(open); if (!open) setEditingCampo(null) }}
        onSubmit={editingCampo ? handleEditCampoSubmit : handleCreateCampo}
        isSubmitting={crearCampo.isPending || editarCampo.isPending}
        campo={editingCampo}
        pasoId={activePasoId}
      />
    </div>
  )
}
